import React from 'react';
import { BentoItem } from './BentoItem';
import { ProcessCard } from './ProcessCard';
import { GrowthChart } from './GrowthChart';
import { PenTool, Video, Scissors, Zap, Upload, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';

export const BentoGrid: React.FC = () => {
  return (
    <div className="w-full">
      <div className="lg:px-24 px-6 md:px-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[minmax(180px,auto)]">
        
        {/* 1. Hero / Intro Card */}
        <BentoItem className="col-span-1 md:col-span-2 row-span-2 min-h-[350px] bg-gradient-to-br from-rose-950/30 to-zinc-900/0" delay={0.1}>
          <div className="flex flex-col justify-between h-full relative z-20 pointer-events-none">
            <div className="p-3 bg-rose-500/10 w-fit rounded-xl backdrop-blur-sm border border-rose-500/10">
               <Zap className="text-primary" size={24} />
            </div>
            
            <div className="mt-auto">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight tracking-tight">
                Your Content <br/>
                <span className="text-primary">Creation Partner</span>
              </h2>
              <p className="text-zinc-400 text-sm md:text-base max-w-sm leading-relaxed mb-8">
                We join your game or server and make dedicated viral content to showcase the experience to potential players.
              </p>
            </div>
          </div>
        </BentoItem>

        {/* 2. Stats 100M+ */}
        <BentoItem className="col-span-1 row-span-1 bg-zinc-950" delay={0.2}>
          <div className="flex flex-col justify-between h-full min-h-[160px]">
            <div>
               <h3 className="text-6xl font-black text-white tracking-tighter flex items-baseline">
                <span>100<span className="text-primary">M</span></span>
                <motion.span 
                  className="text-primary"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.8 }}
                  transition={{ delay: 0.3, duration: 0.5, type: "spring" }}
                >+</motion.span>
               </h3>
            </div>
            
            <div className="flex items-center gap-3 mt-auto relative">
              <motion.div 
                initial={{ height: 0 }}
                whileInView={{ height: "100%" }}
                viewport={{ once: true, amount: 0.8 }}
                transition={{ delay: 0.2, duration: 0.8, ease: "circOut" }}
                className="w-0.5 bg-primary absolute left-0 bottom-0"
              />
              <p className="text-zinc-500 text-sm font-medium leading-tight pl-4">Total views<br/>generated</p>
            </div>
          </div>
        </BentoItem>

        {/* 3. Stats Clients 25+ */}
        <BentoItem className="col-span-1 row-span-1 bg-zinc-950" delay={0.3}>
          <div className="flex flex-col justify-between h-full min-h-[160px]">
            <div className="flex justify-between items-start w-full">
               <h3 className="text-5xl font-black text-white tracking-tighter flex items-baseline">
                 25
                 <motion.span 
                    className="text-primary"
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, amount: 0.8 }}
                    transition={{ delay: 0.3, duration: 0.5, type: "spring" }}
                  >+</motion.span>
               </h3>
               <div className="flex gap-1 text-primary">
                 <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 2 }}>❤️</motion.div>
                 <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 2, delay: 0.5 }}>❤️</motion.div>
               </div>
            </div>
            <p className="text-zinc-500 text-sm font-medium">Completed campaigns</p>
          </div>
        </BentoItem>

        {/* 4. Process Cards */}
        <BentoItem className="col-span-1" delay={0.4}>
          <ProcessCard number="01" title="1. Scripting" description='"Captivating storytelling to build suspense."' icon={PenTool} hasAIAction={false} />
        </BentoItem>
        <BentoItem className="col-span-1" delay={0.5}>
          <ProcessCard number="02" title="2. Filming" description="Gathering fascinating clips for each second of gameplay to build interest." icon={Video} />
        </BentoItem>
        <BentoItem className="col-span-1" delay={0.6}>
          <ProcessCard number="03" title="3. Editing" description="Compiling everything to make the final piece stand out in the feed." icon={Scissors} />
        </BentoItem>
        <BentoItem className="col-span-1" delay={0.7}>
           <ProcessCard number="04" title="4. Uploading" description="Posting your short to TikTok, Instagram and YouTube with optimized captions." icon={Upload} />
        </BentoItem>

        {/* 5. Optimization Chart */}
        <BentoItem className="col-span-1 md:col-span-2 row-span-1" delay={0.8}>
           <div className="flex flex-col h-full justify-between">
             {/* Card Header */}
             <div className="flex flex-col sm:flex-row justify-between items-start gap-3 sm:gap-0 mb-2">
               <div className="flex items-center gap-3">
                 <div className="p-2 sm:p-2.5 bg-rose-500 rounded-xl text-white shadow-lg shadow-rose-900/20">
                   <BarChart3 size={18} className="sm:w-5 sm:h-5" />
                 </div>
                 <div>
                    <h3 className="font-bold text-white text-base sm:text-lg leading-none">Optimization</h3>
                    <p className="text-zinc-500 text-xs mt-1">Analytics & Iteration</p>
                 </div>
               </div>
               
               {/* Stats Top Right */}
               <div className="flex gap-4 sm:gap-6 text-left sm:text-right mr-2">
                 <div>
                   <div className="flex items-baseline sm:justify-end">
                      <span className="text-lg sm:text-xl font-bold text-white">90</span>
                      <motion.span 
                        className="text-primary text-lg sm:text-xl font-bold items-baseline"
                        initial={{ opacity: 0, scale: 0.5 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, amount: 0.8 }}
                        transition={{ delay: 0.3, duration: 0.5, type: "spring" }}
                      >+</motion.span>
                   </div>
                   <span className="text-[9px] sm:text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">POSTS/MO</span>
                 </div>
                 <div>
                   <span className="text-lg sm:text-xl font-bold text-white block">3</span>
                   <span className="text-[9px] sm:text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">PLATFORMS</span>
                 </div>
               </div>
             </div>
             
             {/* Description */}
             <div className="mt-2 text-sm text-zinc-400 leading-relaxed max-w-lg">
                We gather analytics from each post so we can iterate and improve with each video on your game.
             </div>

             {/* Chart Area */}
             <div className="flex-1 w-full min-h-[100px] mt-2">
                <GrowthChart />
             </div>
           </div>
        </BentoItem>

      </div>
      </div>
    </div>
  );
};
